<template>
  <div class="quiz-container">
    <!-- Start Screen -->
    <div v-if="!quiz && !loading" class="start-screen">
      <div class="text-6xl mb-4">🧠</div>
      <h2 class="text-2xl font-bold text-white mb-2">Quiz Battle</h2>
      <p class="text-white/70 text-sm mb-6">Teste dein Food-Wissen und gewinne Rabatte!</p>
      <div class="flex gap-3">
        <button @click="startQuiz('solo')" class="start-btn solo">
          🎯 Solo spielen
        </button>
        <button @click="startQuiz('group')" class="start-btn group-btn">
          👥 Gruppenquiz
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-screen">
      <div class="loader"></div>
      <p class="text-white/70 mt-4">Fragen werden generiert...</p>
    </div>

    <!-- Quiz Active -->
    <div v-if="quiz && !finished" class="quiz-active">
      <!-- Progress -->
      <div class="progress-bar mb-6">
        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{ width: `${((currentIndex + 1) / questions.length) * 100}%` }"
          ></div>
        </div>
        <div class="flex justify-between mt-2 text-sm text-white/60">
          <span>Frage {{ currentIndex + 1 }} / {{ questions.length }}</span>
          <span class="font-bold text-yellow-400">{{ score }} Punkte</span>
        </div>
      </div>

      <!-- Timer -->
      <div class="timer-ring mb-4" :class="{ 'timer-low': timer <= 5 }">
        <svg viewBox="0 0 60 60" class="w-14 h-14">
          <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="4"/>
          <circle
            cx="30" cy="30" r="26"
            fill="none"
            :stroke="timer <= 5 ? '#E71D36' : '#FFD700'"
            stroke-width="4"
            stroke-linecap="round"
            :stroke-dasharray="`${(timer / 15) * 163.36} 163.36`"
            transform="rotate(-90 30 30)"
            class="transition-all duration-1000"
          />
        </svg>
        <span class="timer-text" :class="{ 'text-red-400': timer <= 5 }">{{ timer }}</span>
      </div>

      <!-- Question Card -->
      <div class="question-card" :key="currentIndex">
        <div class="text-3xl mb-3">{{ currentQuestion?.emoji || '❓' }}</div>
        <h3 class="text-lg font-bold text-white mb-6 leading-relaxed">
          {{ currentQuestion?.question }}
        </h3>

        <div class="options-grid">
          <button
            v-for="(opt, oi) in currentQuestion?.options"
            :key="oi"
            @click="answer(oi)"
            :disabled="answered"
            class="option-btn"
            :class="{
              'option-correct': answered && oi === lastCorrectIndex,
              'option-wrong': answered && selectedAnswer === oi && oi !== lastCorrectIndex,
              'option-idle': !answered,
            }"
          >
            <span class="option-letter">{{ ['A', 'B', 'C', 'D'][oi] }}</span>
            <span class="option-text">{{ opt }}</span>
          </button>
        </div>

        <!-- Explanation -->
        <div v-if="answered && lastExplanation" class="explanation-box mt-4">
          <p class="text-sm text-white/80">💡 {{ lastExplanation }}</p>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div v-if="finished" class="results-screen">
      <div class="text-6xl mb-4">{{ scorePercent >= 60 ? '🏆' : '💪' }}</div>
      <h2 class="text-2xl font-bold text-white mb-2">Quiz beendet!</h2>

      <div class="score-display mb-4">
        <div class="score-circle">
          <svg viewBox="0 0 120 120" class="w-28 h-28">
            <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="8"/>
            <circle
              cx="60" cy="60" r="52"
              fill="none"
              :stroke="scorePercent >= 60 ? '#10B981' : '#EF4444'"
              stroke-width="8"
              stroke-linecap="round"
              :stroke-dasharray="`${(scorePercent / 100) * 326.73} 326.73`"
              transform="rotate(-90 60 60)"
            />
          </svg>
          <span class="score-text">{{ scorePercent }}%</span>
        </div>
        <p class="text-white/70 mt-2">{{ score }} / {{ maxScore }} Punkte</p>
      </div>

      <!-- Reward -->
      <div v-if="reward" class="reward-box">
        <div class="text-3xl mb-2">🎁</div>
        <p class="text-yellow-400 font-bold mb-2">{{ reward.message }}</p>
        <button
          v-if="reward.product"
          @click="claimReward"
          class="claim-btn"
        >
          Belohnung einlösen! 🎉
        </button>
      </div>

      <div v-else class="no-reward-box">
        <p class="text-white/60 mb-2">Beim nächsten Mal klappt es bestimmt!</p>
      </div>

      <button @click="resetQuiz" class="retry-btn mt-4">
        Nochmal spielen 🔄
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue'
import { useGamificationStore } from '@/stores/gamification'

const emit = defineEmits(['add-to-cart', 'apply-discount', 'close'])
const gamificationStore = useGamificationStore()

const loading = ref(false)
const quiz = ref(null)
const questions = ref([])
const currentIndex = ref(0)
const score = ref(0)
const maxScore = ref(0)
const answered = ref(false)
const selectedAnswer = ref(null)
const lastCorrectIndex = ref(null)
const lastExplanation = ref(null)
const finished = ref(false)
const scorePercent = ref(0)
const reward = ref(null)
const timer = ref(15)
let timerInterval = null

const currentQuestion = computed(() => questions.value[currentIndex.value])

async function startQuiz(mode) {
  loading.value = true
  try {
    const sessionId = parseInt(localStorage.getItem('sessionNumericId')) || null
    const identifier = localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''
    const data = await gamificationStore.startQuiz(sessionId, identifier, mode)
    if (!data || !data.questions || data.questions.length === 0) {
      loading.value = false
      return
    }
    quiz.value = data
    questions.value = data.questions || []
    maxScore.value = data.maxScore || questions.value.length * 10
    currentIndex.value = 0
    score.value = 0
    answered.value = false
    finished.value = false
    startTimer()
  } catch (e) {
    console.error('Quiz start failed:', e)
    loading.value = false
  } finally {
    loading.value = false
  }
}

function startTimer() {
  clearInterval(timerInterval)
  timer.value = 15
  timerInterval = setInterval(() => {
    timer.value--
    if (timer.value <= 0) {
      clearInterval(timerInterval)
      if (!answered.value) answer(-1)
    }
  }, 1000)
}

async function answer(answerIdx) {
  if (answered.value || !quiz.value?.quizId) return
  answered.value = true
  selectedAnswer.value = answerIdx
  clearInterval(timerInterval)

  try {
    const data = await gamificationStore.answerQuiz(quiz.value.quizId, currentIndex.value, Math.max(0, answerIdx))
    lastCorrectIndex.value = data.correctIndex ?? null
    lastExplanation.value = data.explanation ?? null
    if (data.correct) {
      score.value = data.totalScore || score.value
      if (navigator.vibrate) navigator.vibrate(100)
    } else {
      if (navigator.vibrate) navigator.vibrate([50, 50, 50])
    }

    if (data.finished) {
      setTimeout(() => {
        finished.value = true
        scorePercent.value = data.scorePercent || 0
        reward.value = data.reward || null
      }, 2000)
    } else {
      setTimeout(() => {
        currentIndex.value++
        answered.value = false
        selectedAnswer.value = null
        lastCorrectIndex.value = null
        lastExplanation.value = null
        startTimer()
      }, 2000)
    }
  } catch (e) {
    console.error('Answer submit failed:', e)
    answered.value = false
  }
}

function claimReward() {
  if (reward.value?.product) {
    emit('add-to-cart', reward.value.product)
  } else if (reward.value?.type === 'discount') {
    emit('apply-discount', { ...reward.value, gameName: 'Quiz Battle' })
  }
}

function resetQuiz() {
  quiz.value = null
  questions.value = []
  currentIndex.value = 0
  score.value = 0
  finished.value = false
  reward.value = null
  gamificationStore.resetQuiz()
}

onUnmounted(() => {
  clearInterval(timerInterval)
})
</script>

<style scoped>
.quiz-container {
  @apply flex flex-col items-center py-6 px-4;
}

.start-screen {
  @apply flex flex-col items-center text-center;
}

.start-btn {
  @apply px-6 py-3 text-white font-bold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95;
}
.start-btn.solo {
  @apply bg-gradient-to-r from-purple-600 to-indigo-600;
}
.start-btn.group-btn {
  @apply bg-gradient-to-r from-cyan-500 to-blue-600;
}

.loading-screen {
  @apply flex flex-col items-center py-12;
}
.loader {
  @apply w-12 h-12 border-4 border-white/20 border-t-yellow-400 rounded-full animate-spin;
}

.progress-track {
  @apply w-full h-2 bg-white/10 rounded-full overflow-hidden;
}
.progress-fill {
  @apply h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full transition-all duration-500;
}

.timer-ring {
  @apply relative flex items-center justify-center;
}
.timer-text {
  @apply absolute text-lg font-bold text-white;
}
.timer-low .timer-text {
  animation: pulse 0.5s infinite;
}

.question-card {
  @apply text-center w-full max-w-md p-6 bg-white/5 backdrop-blur-sm rounded-2xl;
  animation: slideIn 0.4s ease-out;
}

.options-grid {
  @apply grid grid-cols-1 gap-3;
}

.option-btn {
  @apply flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-300;
  @apply border border-white/10 bg-white/5;
}
.option-idle {
  @apply hover:bg-white/15 hover:border-white/30 active:scale-[0.98];
}
.option-btn:disabled {
  @apply cursor-default;
}
.option-correct {
  @apply bg-green-500/30 border-green-400 !important;
  animation: correctFlash 0.5s ease;
}
.option-wrong {
  @apply bg-red-500/30 border-red-400 !important;
  animation: shake 0.4s ease;
}

.option-letter {
  @apply w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg text-sm font-bold text-white/80;
}
.option-text {
  @apply text-white text-sm font-medium flex-1;
}

.explanation-box {
  @apply p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl;
}

.score-display {
  @apply flex flex-col items-center;
}
.score-circle {
  @apply relative flex items-center justify-center;
}
.score-text {
  @apply absolute text-2xl font-bold text-white;
}

.reward-box {
  @apply p-5 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl text-center mb-4;
}
.no-reward-box {
  @apply p-4 text-center mb-4;
}
.claim-btn {
  @apply px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold rounded-full;
  @apply hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 transform hover:scale-105;
}
.retry-btn {
  @apply px-6 py-2 bg-white/10 text-white/70 rounded-full text-sm;
  @apply hover:bg-white/20 transition-all duration-300;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes correctFlash {
  0%, 100% { background-color: rgba(16, 185, 129, 0.3); }
  50% { background-color: rgba(16, 185, 129, 0.6); }
}
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-6px); }
  75% { transform: translateX(6px); }
}
@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}
</style>
